import GridLayout from "react-grid-layout";
import { Fragment, useMemo, useRef } from "react";
import { Button, Empty, theme } from "antd";
import {
  ArrowUpOutlined,
  LeftOutlined,
  LoadingOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { DeepLinkType } from "pages/api/deep-links/types";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { useScrollTop } from "src/hooks/useScrollTop";
import { useApplicationContext } from "src/hooks/useApplication";
import { MarkdownViewer } from "./MarkdownViewer";
import { QuestionViewer } from "./QuestionViewer";
import {
  DashboardRuntime,
  DashboardsPageContext,
  isNewDashboardId,
  START_STATE_ID,
  UNSAVED_TITLE,
} from "./context";
import Link from "next/link";
import { useRouter } from "next/router";

export const DEFAULT_ROW_HEIGHT = 48;
export const DEFAULT_COLUMN_COUNT = 12;
export const DEFAULT_SPACING = 16;

export function DashboardViewer(
  props: DashboardsPageContext &
    DashboardRuntime & {
      mode?: "embedded" | "dynamic";
    }
) {
  if ((!props.layout || props.layout.length === 0) && props.isLocked)
    return (
      <BlankDashboard
        dashboardId={props.dashboardId}
        openDashboard={props.openDashboard}
        unlock={props.unlock}
      />
    );

  //expects a fixed width and height
  if (props.mode === "embedded") {
    return (
      <DynamicCanvas>
        {({ width: dynamicWidth, height: dynamicHeight }) => (
          <DashboardViewerContent
            {...props}
            dynamicWidth={dynamicWidth}
            dynamicHeight={dynamicHeight}
          />
        )}
      </DynamicCanvas>
    );
  }

  return <DashboardViewerContent {...props} />;
}

function DashboardViewerContent(
  props: DashboardsPageContext &
    DashboardRuntime & {
      mode?: "embedded" | "dynamic";
      dynamicWidth?: number;
      dynamicHeight?: number;
    }
) {
  const app = useApplicationContext();
  const { token } = theme.useToken();
  const spacing =
    props.extra?.spacing === undefined ? DEFAULT_SPACING : props.extra.spacing;
  const layout = props.layout.sort((a, b) => {
    if (a.y !== b.y) return b.y - a.y;
    return b.x - a.x;
  });
  const [scrollRef, scrollTop] = useScrollTop();
  const parentRef = useRef<HTMLDivElement>(null);

  const hasScrollbars =
    (scrollRef.current?.scrollHeight || 0) >
    (scrollRef.current?.clientHeight || 0);
  const scrollBarWidth = hasScrollbars
    ? (parentRef.current?.clientWidth || 0) -
      (scrollRef.current?.clientWidth || 0)
    : 0;

  const children = useMemo(
    () =>
      layout.map((item) =>
        props.data[item.i] ? (
          <div
            key={item.i}
            style={{
              height: "100%",
              width: "100%",
              overflow: "visible",
            }}
          >
            {props.data[item.i].type === DeepLinkType.EXPLORE ? (
              <QuestionViewer
                colorTheme={props.extra.colorTheme}
                isLocked={props.isLocked}
                cardId={item.i}
                json={{ data: props.data }}
                onRemoveCard={props.onRemoveCard}
                saveDashboard={props.onSave}
                setEditor={props.setEditor}
                searchState={props.searchState}
                setLayout={props.setLayout}
                setData={props.setData} // redundant with below, but things change.
                setCardData={(cardData) => {
                  props.setData((state) => {
                    const questionCard = state[item.i];
                    if (questionCard.type !== DeepLinkType.EXPLORE)
                      throw new Error("Expected question card");
                    state[item.i] = cardData;
                    return state;
                  });
                }}
                source={props.source}
                unlock={props.unlock}
              />
            ) : (
              <MarkdownViewer
                colorTheme={props.extra.colorTheme}
                isLocked={props.isLocked}
                cardId={item.i}
                json={{ data: props.data }}
                setEditor={props.setEditor}
                onRemoveCard={props.onRemoveCard}
                setCardData={(cardData) => {
                  props.setData((state) => {
                    const markdownCard = state[item.i];
                    if (markdownCard.type !== "markdown")
                      throw new Error("Expected markdown card");
                    state[item.i] = cardData;
                    return state;
                  });
                }}
                source={props.source}
              />
            )}
          </div>
        ) : (
          <div
            key={item.i}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              overflow: "visible",
              backgroundColor: token.colorBgLayout,
            }}
          >
            <LoadingOutlined />
          </div>
        )
      ),
    [layout, props, token.colorBgLayout]
  );

  return (
    <Fragment
      key={app.sidebar.isOpen ? "app-sidebar-open" : "app-sidebar-closed"}
    >
      <div
        ref={parentRef}
        style={{
          overflow: "hidden",
          width: props.dynamicWidth || "100%",
          height: props.dynamicHeight || "100%",
          borderBottomRightRadius: 16,
          backgroundColor:
            props.extra.colorTheme === "alternate"
              ? token.colorBgLayout
              : token.colorBgContainer,
        }}
      >
        {scrollTop > 4 && (
          <div
            style={{
              position: "absolute",
              boxShadow: `inset 0 0px 1.5px ${token.colorBgBase}`,
              backgroundColor: "transparent",
              width: "100%",
              zIndex: 1,
              left: 0,
              height: 1,
            }}
          />
        )}
        <div
          key={props.dashboardId}
          ref={scrollRef}
          id="dashboard-scroll-parent"
          className={props.isLocked ? "locked" : "unlocked"}
          style={{
            height: "100%",
            width: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <EmptyHints {...props} />
          <GridLayout
            isDraggable={!props.isLocked}
            isResizable={!props.isLocked}
            layout={props.layout}
            cols={props.extra.columnCount || DEFAULT_COLUMN_COUNT}
            margin={[spacing, spacing]}
            rowHeight={props.extra.rowHeight || DEFAULT_ROW_HEIGHT}
            width={
              (props.dynamicWidth || parentRef.current?.clientWidth || 0) -
              scrollBarWidth
            }
            onLayoutChange={(newLayout: typeof props.layout) => {
              props.setLayout(newLayout);
            }}
            style={{
              backgroundColor:
                props.extra.colorTheme === "alternate"
                  ? token.colorBgLayout
                  : token.colorBgContainer,
            }}
          >
            {children}
          </GridLayout>
        </div>
      </div>
    </Fragment>
  );
}

function EmptyHints(props: DashboardRuntime & DashboardsPageContext) {
  const { token } = theme.useToken();

  return (
    <>
      {props.layout.length === 0 && props.name === UNSAVED_TITLE && (
        <div
          style={{
            position: "absolute",
            left: 16,
            fontSize: 12,
            fontWeight: 600,
            color: token.colorPrimary,
            overflow: "visible",
            zIndex: 10,
            padding: 8,
            letterSpacing: 0.5,
          }}
        >
          <ArrowUpOutlined /> Give your dashboard a memorable name
        </div>
      )}
      {props.layout.length === 0 && (
        <div
          style={{
            position: "absolute",
            right: 118,
            letterSpacing: 0.5,
            fontSize: 12,
            fontWeight: 600,
            color: token.colorPrimary,
            overflow: "visible",
            padding: 8,
          }}
        >
          Start by finding a question to add <ArrowUpOutlined />
        </div>
      )}
      {props.layout.length === 0 && isNewDashboardId(props.dashboardId) && (
        <div style={{ textAlign: "center", paddingTop: 64 }}>
          <Button
            onClick={() => {
              props.lock();
              props.openDashboard(START_STATE_ID);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </>
  );
}

function BlankDashboard(props: {
  openDashboard(id: string): void;
  dashboardId?: string;
  unlock(): void;
}) {
  const { token } = theme.useToken();
  const router = useRouter();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Button type="primary" onClick={() => props.unlock()}>
        <UnlockOutlined />
        Unlock
        {isNewDashboardId(props.dashboardId) ? " to create" : " and edit"}
      </Button>
      <Empty
        style={{
          backgroundColor: token.colorBgContainer,
          padding: 32,
          borderRadius: 16,
        }}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<div>Empty Dashboard</div>}
      />
      {isNewDashboardId(props.dashboardId) && (
        <Link
          href={{
            pathname: "/[orgId]/library",
            query: {
              orgId: router.query.orgId,
              filter: `type:board`,
            },
          }}
        >
          <Button
            type="link"
            onClick={() => props.openDashboard(START_STATE_ID)}
          >
            <LeftOutlined /> back to dashboards
          </Button>
        </Link>
      )}
    </div>
  );
}
